import React, { useState } from "react";
import { dweenColorway } from "utils/constants";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { T } from "services/lang/LanguageService";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { AttributeType } from "@energy-advice/easas-api";
import { ProductTemplateEntryType, useGetProductTemplateData } from "api/api-hooks/product-templates-hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCompanyTimezone } from "hooks/useTimezone";
import DatasetBarChart from "../charts/bar-chart/DatasetBarChart";
import { defaultFromDateTime, defaultToDateTime } from "../charts/utils";
import { PlotDataEx } from "../charts/interfaces";
import {
  AggregationOption,
  aggregationOptions,
  IProductTemplateBarChart,
} from "./product-template-bar-chart-editor/ProductTemplateBarChartTypes";

const formatTimespan = (minutes: number): string => {
  const totalMinutes = Math.round(minutes);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}min`);

  return parts.join(" ");
};

export default function ProductTemplateBarChart(props: IProductTemplateBarChart) {
  const displayTz = useCompanyTimezone();
  const companyTz = useCompanyTimezone();

  const [from, setFrom] = useState<Date>(() => {
    return props.from
      ? new Date(props.from)
      : defaultFromDateTime({
          zoneId: companyTz,
          loadConf: {
            ...props.loadConf,
            alignUnit: props.loadConf?.alignUnit ?? "MINUTES",
            lag: props.loadConf?.lag ?? 6,
            lagUnit: props.loadConf?.lagUnit ?? "HOURS",
            lead: props.loadConf?.lead ?? 0,
            leadUnit: props.loadConf?.leadUnit ?? "HOURS",
          },
          referenceTime: props.referenceTime,
        });
  });

  const [to, setTo] = useState<Date>(() => {
    return props.to
      ? new Date(props.to)
      : defaultToDateTime({
          zoneId: companyTz,
          loadConf: {
            ...props.loadConf,
            alignUnit: props.loadConf?.alignUnit ?? "MINUTES",
            lag: props.loadConf?.lag ?? 6,
            lagUnit: props.loadConf?.lagUnit ?? "HOURS",
            lead: props.loadConf?.lead ?? 0,
            leadUnit: props.loadConf?.leadUnit ?? "HOURS",
          },
          referenceTime: props.referenceTime,
        });
  });

  const [rawFrom, setRawFrom] = useState(from);
  const [rawTo, setRawTo] = useState(to);

  const applyDateRange = () => {
    setFrom(rawFrom);
    setTo(rawTo);
  };

  const params: ProductTemplateEntryType = {
    safeName: props.safeName,
    dateFrom: from.toISOString(),
    dateTo: to.toISOString(),
    filter: props.filter,
  };

  const { data: templateData, isLoading } = useGetProductTemplateData(params, {
    enabled: !!props.safeName && !!from && !!to,
  });

  if (isLoading)
    return (
      <div className="demo-container p-p-4">
        <ProgressSpinner className="p-d-block p-mx-auto" />
      </div>
    );

  const nameToAggregate = new Map<
    string,
    { total: number; count: number; min: number; max: number; isTimespan?: boolean }
  >();

  templateData?.forEach(entry => {
    const timeStr = entry.entries?.[props.timeColumn]?.value;
    const name = entry.entries?.[props.nameColumn]?.value?.toString();
    const entrie = entry.entries?.[props.valueColumn];

    if (!entrie || !timeStr || !name || typeof timeStr !== "string") return;

    const entryTime = new Date(timeStr);
    if (entryTime < from || entryTime > to) return;

    if (entrie.type === AttributeType.DOUBLE || entrie.type === AttributeType.LONG) {
      const value = Number(entrie.value);
      if (Number.isNaN(value)) return;

      const existing = nameToAggregate.get(name) ?? {
        total: 0,
        count: 0,
        min: value,
        max: value,
        isTimespan: false,
      };

      existing.total += value;
      existing.count += 1;
      existing.min = Math.min(existing.min, value);
      existing.max = Math.max(existing.max, value);

      nameToAggregate.set(name, existing);
    }

    if (entrie.type === AttributeType.TIMESPAN) {
      const t = entrie.value;
      if (
        typeof t === "object" &&
        t !== null &&
        "days" in t &&
        "hours" in t &&
        "minutes" in t &&
        "seconds" in t
      ) {
        const valueInMinutes =
          (t.days ?? 0) * 24 * 60 + (t.hours ?? 0) * 60 + (t.minutes ?? 0) + (t.seconds ?? 0) / 60;

        const existing = nameToAggregate.get(name) ?? {
          total: 0,
          count: 0,
          min: valueInMinutes,
          max: valueInMinutes,
          isTimespan: true,
        };

        existing.total += valueInMinutes;
        existing.count += 1;
        existing.min = Math.min(existing.min, valueInMinutes);
        existing.max = Math.max(existing.max, valueInMinutes);
        existing.isTimespan = true;

        nameToAggregate.set(name, existing);
      }
    }
  });
  const getHoverTemplateValue = (yValue: number, isTimespan: boolean) => {
    if (props.aggregationType === AggregationOption.COUNT) {
      return `%{y}, %{x}<br><extra></extra>`;
    }

    if (isTimespan) {
      return `${formatTimespan(yValue)}, %{x}<br><extra></extra>`;
    }

    return `%{y} ${props.valueUnit}, %{x}<br><extra></extra>`;
  };

  const sortedEntries = Array.from(nameToAggregate.entries()).sort(([a], [b]) => Number(a) - Number(b));

  const plotData: Partial<PlotDataEx>[] = sortedEntries.map(([name, agg], index) => {
    let yValue: number;
    let displayText: string;

    const isTimespan = agg.isTimespan ?? false;

    switch (props.aggregationType) {
      case AggregationOption.AVG:
        yValue = agg.total / agg.count;
        break;
      case AggregationOption.MIN:
        yValue = agg.min;
        break;
      case AggregationOption.MAX:
        yValue = agg.max;
        break;
      case AggregationOption.COUNT:
        yValue = agg.count;
        break;
      case AggregationOption.SUM:
      default:
        yValue = agg.total;
        break;
    }

    if (props.aggregationType === AggregationOption.COUNT) {
      displayText = `${yValue}`;
    } else if (isTimespan) {
      displayText = formatTimespan(yValue);
    } else {
      displayText = `${yValue.toFixed(props.decimalPlaces ?? 2)} ${props.valueUnit}`;
    }
    return {
      hovertemplate: getHoverTemplateValue(yValue, isTimespan),
      marker: {
        color: dweenColorway[index % dweenColorway.length],
      },
      text: [displayText],
      type: "bar",
      unit: props.valueUnit,
      x: [name],
      y: [yValue],
      name,
    };
  });

  const yMin = Math.min(
    ...sortedEntries.map(([, agg]) => {
      switch (props.aggregationType) {
        case AggregationOption.MIN:
          return agg.min;
        case AggregationOption.MAX:
          return agg.max;
        case AggregationOption.AVG:
          return agg.total / agg.count;
        case AggregationOption.COUNT:
          return agg.count;
        default:
          return agg.total;
      }
    })
  );

  const yMax = Math.max(
    ...sortedEntries.map(([, agg]) => {
      switch (props.aggregationType) {
        case AggregationOption.MIN:
          return agg.min;
        case AggregationOption.MAX:
          return agg.max;
        case AggregationOption.AVG:
          return agg.total / agg.count;
        case AggregationOption.COUNT:
          return agg.count;
        default:
          return agg.total;
      }
    })
  );

  function generateFormattedTicks(
    min: number,
    max: number,
    step: number = 1
  ): { tickvals: number[]; ticktext: string[] } {
    const tickvals: number[] = [];
    const ticktext: string[] = [];
    const range = max - min;

    const approxTickCount = 5;
    const rawStep = range / approxTickCount || 1;
    const roundedStep = Math.max(Math.round(rawStep / step) * step, 1);

    for (let val = Math.floor(min); val <= Math.ceil(max); val += roundedStep) {
      tickvals.push(val);
      ticktext.push(formatTimespan(val));
    }

    return { tickvals, ticktext };
  }

  const timespanTickConfig = generateFormattedTicks(yMin, yMax);
  const hasTimespan = [...nameToAggregate.values()].some(agg => agg.isTimespan);
  return (
    <>
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-row gap-2">
          <CalendarWrapper value={rawFrom} onChange={setRawFrom} />
          <CalendarWrapper value={rawTo} onChange={setRawTo} />
          <Button
            icon="pi pi-sync"
            className="component-editor-button h-full"
            tooltip={T("action.apply-date-range")}
            tooltipOptions={{ position: "bottom" }}
            onClick={applyDateRange}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <span className="font-semibold text-color-secondary">{T("label.aggregation-type")}:</span>
          <Tag
            value={aggregationOptions.find(opt => opt.value === props.aggregationType)?.label ?? "Sum"}
            className="px-3 py-1 text-sm component-editor-button"
          />
        </div>
      </div>
      <DatasetBarChart
        data={plotData as PlotDataEx[]}
        height={props.height ?? 400}
        width={props.width}
        xaxisType="array"
        skipWarning
        hideLegend={props.hideLegend}
        xAxisTitle={props.xAxisTitle}
        yTickConfig={hasTimespan ? timespanTickConfig : undefined}
      />
    </>
  );
}
